/**
 * Helpers + types for the live Visitor Map.
 *
 * The map view talks to a different backend contract than the legacy
 * `useVisitors` list hook (which hits `/visitors` + `/visitors/stats/summary`).
 * The map uses the geo-aware endpoints:
 *   - GET /api/visitors/live
 *   - GET /api/visitors/search
 *   - GET /api/visitors/:id
 *
 * So we define a dedicated `MapVisitor` shape here rather than reuse the
 * list hook's `Visitor` interface (which carries unrelated fields and lacks
 * lat/lng/accuracy).
 */

export interface MapVisitor {
  id: string
  status: 'online' | 'offline'
  is_online: boolean
  name: string | null
  email: string | null
  account_alias: string | null
  company_name: string | null
  company_logo_url: string | null
  company_country: string | null
  city: string | null
  region: string | null
  country: string | null
  country_code: string | null
  latitude: number | null
  longitude: number | null
  accuracy_radius: number | null
  current_page_url: string | null
  current_page_title: string | null
  connected_at: string | null
  last_seen_at: string | null
  disconnected_at: string | null
  browser: string | null
  os: string | null
  device_type: string | null
  lead_score: number
}

export interface VisitorPageView {
  page_url: string | null
  page_title: string | null
  viewed_at: string | null
  duration_seconds?: number | null
}

export interface MapVisitorDetail extends MapVisitor {
  page_views?: VisitorPageView[]
}

export interface LiveVisitorsResponse {
  visitors: MapVisitor[]
  count: number
}

export interface VisitorSearchResponse {
  visitors: MapVisitor[]
  total: number
  page: number
  pageSize: number
}

/** True when the visitor is currently online. */
export function isVisitorOnline(v: Pick<MapVisitor, 'status' | 'is_online'>): boolean {
  return v.status === 'online' || v.is_online
}

/**
 * Sort the roster the way the product owner wants it (and the way the backend
 * returns it on first load): ONLINE first, then OFFLINE ordered by most-recently
 * seen. Enforced client-side so the order stays correct after socket updates.
 */
export function sortRoster(visitors: MapVisitor[]): MapVisitor[] {
  const ts = (iso: string | null | undefined): number => {
    if (!iso) return 0
    const t = new Date(iso).getTime()
    return Number.isNaN(t) ? 0 : t
  }
  return visitors.slice().sort((a, b) => {
    const aOnline = isVisitorOnline(a)
    const bOnline = isVisitorOnline(b)
    if (aOnline !== bOnline) return aOnline ? -1 : 1
    // Within the same group, most recently seen first.
    return ts(b.last_seen_at) - ts(a.last_seen_at)
  })
}

/** A visitor we can actually plot (lat/lng are present). */
export type PlottableVisitor = MapVisitor & { latitude: number; longitude: number }

export function isPlottable(v: MapVisitor): v is PlottableVisitor {
  return (
    typeof v.latitude === 'number' &&
    typeof v.longitude === 'number' &&
    !Number.isNaN(v.latitude) &&
    !Number.isNaN(v.longitude)
  )
}

/** The visitor's display name (falls back to "Anonymous visitor"). */
export function visitorDisplayName(
  v: Pick<MapVisitor, 'name'>
): string {
  return v.name && v.name.trim() ? v.name.trim() : 'Anonymous visitor'
}

/**
 * Initials for the avatar fallback. Prefers the visitor's name, then the
 * organization name; "?" when neither is present.
 */
export function visitorInitials(
  v: Pick<MapVisitor, 'name' | 'company_name'>
): string {
  const source = (v.name && v.name.trim()) || (v.company_name && v.company_name.trim()) || ''
  if (!source) return '?'
  const words = source.split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

/** Human-readable "City, Country" (falls back to whatever is present). */
export function visitorLocationLabel(v: Pick<MapVisitor, 'city' | 'region' | 'country'>): string {
  const parts = [v.city, v.country].filter((p): p is string => Boolean(p && p.trim()))
  if (parts.length) return parts.join(', ')
  if (v.region) return v.region
  return 'Unknown location'
}

/**
 * Turn a raw page URL into a friendly label.
 *
 * Prefers an explicit page title, otherwise shows the path WITHOUT the query
 * string (so we never dump `?utm_source=…&token=…` into the UI). Falls back to
 * the host when the path is just "/".
 */
export function friendlyPageLabel(
  url: string | null | undefined,
  title?: string | null
): string {
  if (title && title.trim()) return title.trim()
  if (!url || !url.trim()) return 'Unknown page'

  try {
    const u = new URL(url)
    const path = u.pathname.replace(/\/+$/, '')
    if (!path || path === '') return u.hostname
    return path
  } catch {
    // Not an absolute URL — strip any query string manually and return the path.
    const noQuery = url.split('?')[0].split('#')[0]
    return noQuery || url
  }
}

/**
 * Format an ISO timestamp in the agent's locale + show their timezone label,
 * e.g. "17 Jun 2026, 10:42 (BST)".
 */
export function formatLocalDateTimeWithTz(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'

  const datePart = d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  let tz = ''
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(d)
    tz = parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
  } catch {
    tz = ''
  }

  return tz ? `${datePart} (${tz})` : datePart
}

/** Format a number of seconds as HH:MM:SS (always 2-digit fields). */
export function formatHMS(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(s / 3600)
  const minutes = Math.floor((s % 3600) / 60)
  const seconds = s % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

/** Seconds elapsed since an ISO timestamp (>= 0). Returns null if unparseable. */
export function secondsSince(iso: string | null | undefined): number | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return null
  return Math.max(0, Math.floor((Date.now() - t) / 1000))
}

/** Session length between connect and disconnect, in seconds (or null). */
export function sessionLengthSeconds(
  connectedAt: string | null | undefined,
  disconnectedAt: string | null | undefined
): number | null {
  if (!connectedAt || !disconnectedAt) return null
  const a = new Date(connectedAt).getTime()
  const b = new Date(disconnectedAt).getTime()
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return null
  return Math.floor((b - a) / 1000)
}
